import React from 'react';
import ReactECharts from 'echarts-for-react';
import { calcDomainScore, getTier, getOverallScore } from '../../utils/scoring';

const Insights = ({ setPage, scores }) => {
  const printScore = calcDomainScore(scores, 'P');
  const itScore = calcDomainScore(scores, 'I');
  const cyberScore = calcDomainScore(scores, 'C');
  const overallScore = getOverallScore(scores);
  const tier = getTier(overallScore);

  const interpretations = {
    1: 'Your environment is currently in reactive mode with significant gaps in visibility, security, and operational efficiency. Immediate attention is needed to reduce risk and establish foundational controls.',
    2: 'Your organization has some foundational elements in place but is still operating reactively in several areas. There are clear opportunities to standardize processes and reduce risk.',
    3: 'Your environment shows developing alignment with good foundational elements. Focus areas remain for optimization and strengthening security posture.',
    4: 'Your technology environment is well-aligned with strong processes and controls. Focus on continuous improvement and advanced capabilities.',
    5: 'Your organization demonstrates industry-leading technology alignment with mature processes across all domains.'
  };

  const radarOptions = {
    radar: {
      indicator: [
        { name: 'Print', max: 5 },
        { name: 'IT', max: 5 },
        { name: 'Security', max: 5 }
      ],
      axisName: { color: '#4A4A4A', fontWeight: 'bold' },
      splitArea: { show: false }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [printScore, itScore, cyberScore],
          name: 'Current',
          itemStyle: { color: '#2E5FA7' },
          areaStyle: { color: 'rgba(46, 95, 167, 0.3)' },
          lineStyle: { width: 2 }
        },
        {
          value: [4, 4, 4],
          name: 'Target',
          itemStyle: { color: '#28A745' },
          lineStyle: { type: 'dashed', width: 2 },
          areaStyle: { color: 'rgba(40, 167, 69, 0.1)' }
        }
      ]
    }],
    legend: { bottom: 0, data: ['Current', 'Target'] }
  };

  const getGaps = () => {
    const gaps = [];
    if (printScore < 3) {
      gaps.push({
        title: '🖨️ Print Environment',
        severity: printScore < 2 ? 'critical' : 'moderate',
        content: 'Limited visibility and control over print fleet, resulting in cost leakage and workflow inefficiencies.',
        solutions: ['Managed Print Services', 'Automated Supply Routing', 'Secure Print Release', 'Workflow Digitization']
      });
    }
    if (itScore < 3) {
      gaps.push({
        title: '💻 IT Infrastructure',
        severity: itScore < 2 ? 'critical' : 'moderate',
        content: 'Reactive support model with gaps in endpoint management, cloud adoption, or disaster recovery.',
        solutions: ['24/7/365 Managed IT Services', 'Cloud Migration & Azure/AWS Support', 'Backup & Disaster Recovery (BCDR)', 'Unified Communications (VoIP)']
      });
    }
    if (cyberScore < 3) {
      gaps.push({
        title: '🔒 Security Posture',
        severity: cyberScore < 2 ? 'critical' : 'moderate',
        content: 'Security gaps in endpoint protection, access controls, or lack of strategic security leadership.',
        solutions: ['vCISO Strategic Guidance', '24/7 Security Operations Center (SOC)', 'EDR/XDR Endpoint Protection', 'Continuous Security Awareness Training']
      });
    }
    if (scores.C3 && scores.C3 < 3) {
      gaps.unshift({
        title: '🚨 Critical: MFA Not Implemented',
        severity: 'critical',
        content: 'Without MFA, your accounts are vulnerable to 99.9% of credential-based attacks.',
        solutions: ['Immediate MFA deployment across all systems and users via Identity Access Management (IAM)']
      });
    }
    if (gaps.length === 0) {
      gaps.push({
        title: '✅ Well Aligned',
        severity: 'good',
        content: 'Your technology environment shows strong alignment across all domains.',
        solutions: ['Continue proactive monitoring', 'Focus on vCIO/vCISO strategic optimization', 'Explore advanced AI and automation capabilities']
      });
    }
    return gaps;
  };

  const getImpacts = () => {
    const impacts = [];
    if (printScore < 3) {
      impacts.push({ icon: '💰', text: 'Cost leakage from unmanaged print environment and manual workflows' });
      impacts.push({ icon: '⏱️', text: 'Productivity loss from print-related issues and vendor finger-pointing' });
    }
    if (itScore < 3) {
      impacts.push({ icon: '📉', text: 'Higher downtime risk from reactive, break-fix support models' });
      impacts.push({ icon: '🔧', text: 'Internal staff focused on firefighting vs. strategic business growth' });
    }
    if (cyberScore < 3) {
      impacts.push({ icon: '🚨', text: 'Elevated security exposure, ransomware risk, and lack of incident readiness' });
      impacts.push({ icon: '📋', text: 'Potential compliance failures (HIPAA, CMMC, PCI, etc.)' });
    }
    if (scores.I5 && scores.I5 < 3) {
      impacts.push({ icon: '🎯', text: 'Technology strategy is disconnected from overall business objectives' });
    }
    return impacts;
  };

  const renderDomainBar = (name, icon, score, color) => (
    <div className="flex items-center gap-4 p-4 bg-[#F4F6F9] rounded-xl mb-3">
      <div className="text-3xl">{icon}</div>
      <div className="flex-1">
        <div className="font-semibold mb-1">{name}</div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${(score / 5) * 100}%`, backgroundColor: color }}></div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-2xl font-bold" style={{ color }}>{score}</div>
        <div className="text-[11px] text-gray-500">/ 5.0</div>
      </div>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-6">
      <div className="text-center p-10 bg-gradient-to-br from-[#F4F6F9] to-white rounded-2xl shadow-sm border border-gray-100">
        <div className="text-sm uppercase tracking-widest text-gray-500 mb-2 font-bold">Technology Alignment Score</div>
        <div className="text-7xl font-bold text-[#2E5FA7] leading-none mb-2">{overallScore}</div>
        <div className="text-gray-500 mb-4 font-medium">out of 5.0</div>
        <div className="inline-block px-6 py-2.5 rounded-full font-bold text-white text-sm shadow-sm" style={{ backgroundColor: tier.color }}>
          {tier.name}
        </div>
        <p className="max-w-2xl mx-auto mt-6 text-gray-700 leading-relaxed">
          {interpretations[tier.level]}
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold text-[#2E5FA7] mb-6">📊 Domain Breakdown</h2>
        <div className="flex flex-wrap md:flex-nowrap gap-8 items-center justify-center">
          <div className="w-full md:w-1/2 max-w-[320px]">
            <ReactECharts option={radarOptions} style={{ height: '300px' }} />
          </div>
          <div className="w-full md:w-1/2">
            {renderDomainBar('Print', '🖨️', printScore, '#2E5FA7')}
            {renderDomainBar('IT', '💻', itScore, '#1a5f7a')}
            {renderDomainBar('Security', '🔒', cyberScore, '#6b2d5b')}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold text-[#2E5FA7] mb-6">⚠️ Key Gaps & Risks Identified</h2>
        <div className="space-y-4">
          {getGaps().map((gap, i) => (
            <div key={i} className={`bg-white rounded-xl p-5 border border-gray-200 border-l-4 ${gap.severity === 'critical' ? 'border-l-[#DC3545]' : gap.severity === 'moderate' ? 'border-l-[#FFC107]' : 'border-l-[#28A745]'}`}>
              <div className="flex justify-between items-center mb-3">
                <div className="font-bold text-lg text-gray-800">{gap.title}</div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase
                  ${gap.severity === 'critical' ? 'bg-[#f8d7da] text-[#DC3545]' : gap.severity === 'moderate' ? 'bg-[#fff3cd] text-[#856404]' : 'bg-[#d4edda] text-[#28A745]'}`}>
                  {gap.severity}
                </span>
              </div>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">{gap.content}</p>
              <div className="bg-[#F4F6F9] rounded-lg p-4">
                <div className="text-[11px] font-bold text-[#2E5FA7] uppercase tracking-wider mb-3">Visual Edge IT Solutions</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {gap.solutions.map((sol, j) => (
                    <div key={j} className="text-sm flex items-start gap-2 text-gray-700 font-medium">
                      <span className="text-[#2E5FA7] mt-0.5">✓</span> {sol}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full">
          <h2 className="text-xl font-bold text-[#2E5FA7] mb-6">💼 Business Impact</h2>
          <div className="space-y-3">
            {getImpacts().length > 0 ? getImpacts().map((impact, i) => (
              <div key={i} className="flex items-start gap-3 p-4 bg-[#F4F6F9] rounded-xl text-gray-800 border border-gray-100">
                <span className="text-xl mt-0.5">{impact.icon}</span>
                <span className="text-sm font-medium leading-relaxed">{impact.text}</span>
              </div>
            )) : (
              <div className="p-4 bg-[#d4edda] text-[#155724] rounded-xl text-center font-bold border border-[#c3e6cb]">
                Your environment shows strong alignment with minimal business impact concerns.
              </div>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#2E5FA7] to-[#244C86] rounded-2xl p-8 shadow-sm text-white h-full">
          <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
            ⭐ The Total Technology Advantage
          </h2>
          <p className="text-sm text-blue-100 mb-6 leading-relaxed">
            Partnering with a Total Technology Provider means eliminating the friction of managing disparate IT, security, and print vendors. 
          </p>
          <ul className="space-y-4 text-sm font-medium">
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">🤝</div>
              <span>One single point of accountability</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">🛡️</div>
              <span>Integrated security across all endpoints</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">📞</div>
              <span>24/7/365 US-based support (NOC & SOC)</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">🗺️</div>
              <span>Unified strategic roadmap (vCIO & vCISO)</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
        <h2 className="text-2xl font-bold text-[#2E5FA7] mb-6">✅ Comprehensive Portfolio Alignment</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-6 bg-gradient-to-br from-[#e8f4fc] to-white rounded-xl border-t-4 border-[#2E5FA7] shadow-sm">
            <div className="font-bold text-[#2E5FA7] text-lg mb-4 flex items-center gap-2">🖨️ Managed Print</div>
            <ul className="text-sm space-y-2.5 text-gray-700 font-medium">
              <li className="flex items-start gap-2"><span className="text-[#2E5FA7]">►</span> Managed Print Services</li>
              <li className="flex items-start gap-2"><span className="text-[#2E5FA7]">►</span> Automated Supply Fulfillment</li>
              <li className="flex items-start gap-2"><span className="text-[#2E5FA7]">►</span> Secure Print Release</li>
              <li className="flex items-start gap-2"><span className="text-[#2E5FA7]">►</span> Document Management</li>
            </ul>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-[#e8f8f5] to-white rounded-xl border-t-4 border-[#1a5f7a] shadow-sm">
            <div className="font-bold text-[#1a5f7a] text-lg mb-4 flex items-center gap-2">💻 Managed IT</div>
            <ul className="text-sm space-y-2.5 text-gray-700 font-medium">
              <li className="flex items-start gap-2"><span className="text-[#1a5f7a]">►</span> 24/7/365 Helpdesk Support</li>
              <li className="flex items-start gap-2"><span className="text-[#1a5f7a]">►</span> Cloud & Server Management</li>
              <li className="flex items-start gap-2"><span className="text-[#1a5f7a]">►</span> Backup & Disaster Recovery (BCDR)</li>
              <li className="flex items-start gap-2"><span className="text-[#1a5f7a]">►</span> Unified Communications (VoIP)</li>
            </ul>
          </div>
          
          <div className="p-6 bg-gradient-to-br from-[#f5e8f5] to-white rounded-xl border-t-4 border-[#6b2d5b] shadow-sm">
            <div className="font-bold text-[#6b2d5b] text-lg mb-4 flex items-center gap-2">🔒 Cybersecurity</div>
            <ul className="text-sm space-y-2.5 text-gray-700 font-medium">
              <li className="flex items-start gap-2"><span className="text-[#6b2d5b]">►</span> Virtual CISO (vCISO) Services</li>
              <li className="flex items-start gap-2"><span className="text-[#6b2d5b]">►</span> 24/7 Security Operations Center</li>
              <li className="flex items-start gap-2"><span className="text-[#6b2d5b]">►</span> EDR/XDR Endpoint Protection</li>
              <li className="flex items-start gap-2"><span className="text-[#6b2d5b]">►</span> Compliance & Risk Management</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pb-8">
        <button onClick={() => setPage('alignment')} className="px-6 py-3 rounded-lg font-bold text-[#2E5FA7] border-2 border-[#2E5FA7] hover:bg-gray-50 transition-all">← Back</button>
        <button onClick={() => setPage('roadmap')} className="px-6 py-3 rounded-lg font-bold bg-[#2E5FA7] text-white hover:bg-[#244C86] transition-all shadow-md">View Roadmap →</button>
      </div>
    </div>
  );
};

export default Insights;